import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

// Hook adaptado para usar formulario_plano_acao_id em vez de plano_acao_id

interface Acao5W2HFormulario {
  id: string;
  formulario_plano_acao_id: string;
  what_oque: string;
  why_porque: string;
  who_quem?: string | null;
  where_onde?: string | null;
  when_quando_inicio?: string | null;
  when_quando_fim?: string | null;
  how_como?: string | null;
  how_much_quanto?: number | null;
  status: string;
  prioridade?: string | null;
  ordem?: number | null;
  percentual_conclusao?: number | null;
  motivo_bloqueio?: string | null;
  data_inicio_real?: string | null;
  data_conclusao_real?: string | null;
  custo_real?: number | null;
  resultado_obtido?: string | null;
  criado_por?: string | null;
  criado_em: string;
  atualizado_em: string;
  responsavel?: { id: string; name: string } | null;
}

interface Acao5W2HFormularioInsert {
  formulario_plano_acao_id: string;
  what_oque: string;
  why_porque: string;
  who_quem?: string | null;
  where_onde?: string | null;
  when_quando_inicio?: string | null;
  when_quando_fim?: string | null;
  how_como?: string | null;
  how_much_quanto?: number | null;
  status?: string;
  prioridade?: string | null;
  ordem?: number | null;
}

interface Acao5W2HFormularioUpdate {
  id: string;
  what_oque?: string;
  why_porque?: string;
  who_quem?: string | null;
  where_onde?: string | null;
  when_quando_inicio?: string | null;
  when_quando_fim?: string | null;
  how_como?: string | null;
  how_much_quanto?: number | null;
  status?: string;
  prioridade?: string | null;
  ordem?: number | null;
  percentual_conclusao?: number | null;
  motivo_bloqueio?: string | null;
  data_inicio_real?: string | null;
  data_conclusao_real?: string | null;
  custo_real?: number | null;
  resultado_obtido?: string | null;
}

export function useAcoes5W2HFormulario(formularioPlanoAcaoId: string | undefined) {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: ['acoes-5w2h-formulario', formularioPlanoAcaoId],
    queryFn: async () => {
      if (!formularioPlanoAcaoId) return [];
      const { data, error } = await supabase
        .from('acoes_5w2h')
        .select(`
          *,
          responsavel:users!acoes_5w2h_who_quem_fkey(id, name)
        `)
        .eq('formulario_plano_acao_id', formularioPlanoAcaoId)
        .order('ordem', { ascending: true });
      if (error) throw error;
      return data as Acao5W2HFormulario[];
    },
    enabled: !!formularioPlanoAcaoId
  });

  const createMutation = useMutation({
    mutationFn: async (data: Acao5W2HFormularioInsert) => {
      // Obter próxima ordem
      const { data: existing } = await supabase
        .from('acoes_5w2h')
        .select('ordem')
        .eq('formulario_plano_acao_id', data.formulario_plano_acao_id)
        .order('ordem', { ascending: false })
        .limit(1);
      
      const nextOrdem = (existing?.[0]?.ordem ?? 0) + 1;

      // Criar payload compatível
      const payload: Record<string, unknown> = {
        what_oque: data.what_oque,
        why_porque: data.why_porque,
        who_quem: data.who_quem,
        where_onde: data.where_onde,
        when_quando_inicio: data.when_quando_inicio,
        when_quando_fim: data.when_quando_fim,
        how_como: data.how_como,
        how_much_quanto: data.how_much_quanto,
        status: data.status || 'pendente',
        prioridade: data.prioridade,
        formulario_plano_acao_id: data.formulario_plano_acao_id,
        ordem: nextOrdem
      };

      const { data: result, error } = await supabase
        .from('acoes_5w2h')
        .insert(payload as any)
        .select()
        .single();
      if (error) throw error;
      return result;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['acoes-5w2h-formulario', formularioPlanoAcaoId] });
      queryClient.invalidateQueries({ queryKey: ['formulario-planos-acao'] });
      toast({ title: 'Ação criada com sucesso' });
    },
    onError: (error: Error) => {
      toast({ title: 'Erro ao criar ação', description: error.message, variant: 'destructive' });
    }
  });

  const updateMutation = useMutation({
    mutationFn: async ({ id, ...data }: Acao5W2HFormularioUpdate) => {
      const { data: result, error } = await supabase
        .from('acoes_5w2h')
        .update(data as any)
        .eq('id', id)
        .select()
        .single();
      if (error) throw error;
      return result;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['acoes-5w2h-formulario', formularioPlanoAcaoId] });
      queryClient.invalidateQueries({ queryKey: ['formulario-planos-acao'] });
      toast({ title: 'Ação atualizada com sucesso' });
    },
    onError: (error: Error) => {
      toast({ title: 'Erro ao atualizar ação', description: error.message, variant: 'destructive' });
    }
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from('acoes_5w2h').delete().eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['acoes-5w2h-formulario', formularioPlanoAcaoId] });
      queryClient.invalidateQueries({ queryKey: ['formulario-planos-acao'] });
      toast({ title: 'Ação excluída' });
    },
    onError: (error: Error) => {
      toast({ title: 'Erro ao excluir ação', description: error.message, variant: 'destructive' });
    }
  });

  // Atualizar status (para Kanban)
  const updateStatusMutation = useMutation({
    mutationFn: async ({ id, status }: { id: string; status: string }) => {
      const updateData: Partial<Acao5W2HFormularioUpdate> = { status };
      
      // Se concluída, registrar data
      if (status === 'concluida') {
        updateData.data_conclusao_real = new Date().toISOString();
        updateData.percentual_conclusao = 100;
      } else if (status === 'em_andamento') {
        updateData.data_inicio_real = new Date().toISOString();
      }

      const { data: result, error } = await supabase
        .from('acoes_5w2h')
        .update(updateData)
        .eq('id', id)
        .select()
        .single();
      if (error) throw error;
      return result;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['acoes-5w2h-formulario', formularioPlanoAcaoId] });
      queryClient.invalidateQueries({ queryKey: ['formulario-planos-acao'] });
    },
    onError: (error: Error) => {
      toast({ title: 'Erro ao atualizar status', description: error.message, variant: 'destructive' });
    }
  });

  // Reordenar ações
  const reorderMutation = useMutation({
    mutationFn: async (orderedIds: string[]) => {
      const updates = orderedIds.map((id, index) => 
        supabase.from('acoes_5w2h').update({ ordem: index + 1 }).eq('id', id)
      );
      await Promise.all(updates);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['acoes-5w2h-formulario', formularioPlanoAcaoId] });
    },
    onError: (error: Error) => {
      toast({ title: 'Erro ao reordenar', description: error.message, variant: 'destructive' });
    }
  });

  return {
    acoes: query.data ?? [],
    isLoading: query.isLoading,
    error: query.error,
    createAcao: createMutation.mutate,
    updateAcao: updateMutation.mutate,
    deleteAcao: deleteMutation.mutate,
    updateStatus: updateStatusMutation.mutate,
    reorderAcoes: reorderMutation.mutate,
    isCreating: createMutation.isPending,
    isUpdating: updateMutation.isPending,
    isDeleting: deleteMutation.isPending
  };
}
